import {
	AuditLogSearch,
	Clinic,
	Doctor,
	PatientSearchResponse
} from "../../models";
import { FaceToFaceDoctorSearch, SelectControl } from "../Common";
import { AuditLogPatientSearch } from "./AuditLogPatientSearch";
import { AuditLogUserSearch } from "./AuditLogUserSearch";
import { TextField } from "@mui/material";
import { format } from "date-fns/format";

interface params {
	filters: AuditLogSearch;
	clinics: Clinic[];
	screens: string[];
	actions: string[];
	setFilters: (auditLogSearch: AuditLogSearch) => void;
	handleSearchClick: () => void;
	doctorSearch?: Doctor;
	setDoctorSearch: React.Dispatch<React.SetStateAction<Doctor | undefined>>;
	patientSearch?: PatientSearchResponse;
	setPatientSearch: React.Dispatch<
		React.SetStateAction<PatientSearchResponse | undefined>
	>;
	usernameSearch: string;
	setUsernameSearch: React.Dispatch<React.SetStateAction<string>>;
	handleClearClick: () => void;
}

export const AuditLogsFilter = ({
	filters,
	clinics,
	screens,
	actions,
	setFilters,
	handleSearchClick,
	doctorSearch,
	setDoctorSearch,
	patientSearch,
	setPatientSearch,
	usernameSearch,
	setUsernameSearch,
	handleClearClick
}: params) => {
	const today = format(new Date(), "yyyy-MM-dd");
	const fromDateValue = filters?.fromDate?.substring(0, 10) ?? "";
	const toDateValue = filters?.toDate?.substring(0, 10) ?? "";
	const toDateValidationError =
		toDateValue !== "" && fromDateValue !== "" && toDateValue < fromDateValue
			? "To date cannot be before from date"
			: toDateValue !== "" && toDateValue > today
				? "To date cannot be greater than today"
				: "";

	const handleSelectChange = (id: string, value: any) => {
		let convertedValue =
			value && value !== "none" && value !== "" ? value : null;
		const newFilters = {
			...filters,
			[id]: convertedValue
		};
		setFilters(newFilters);
	};

	const handleDoctorChange = (doctor?: Doctor) => {
		const newFilters = {
			...filters,
			doctorId: doctor?.id
		};
		setDoctorSearch(doctor);
		setFilters(newFilters);
	};

	const handleUsernameChange = (username?: string) => {
		const newFilters = {
			...filters,
			username: username ?? ""
		};
		setUsernameSearch(username ?? "");
		setFilters(newFilters);
	};

	const handlePatientChange = (patient?: PatientSearchResponse) => {
		const newFilters = {
			...filters,
			patientId: patient?.patientId,
			patientName: patient?.patientName
		};
		setPatientSearch(patient);
		setFilters(newFilters);
	};

	const handleFiltersChange = (e: any): void => {
		let { name, value } = e.target;
		if (name === "toDate" && value !== "") {
			value = `${value}T23:59:59`;
		}
		const newFilters = {
			...filters,
			[name]: value !== "" ? value : null
		};
		setFilters(newFilters);
	};

	const clearFilter = (name: string, value?: any): void => {
		const newFilters = {
			...filters,
			[name]: value ?? null
		};
		setFilters(newFilters);
	};

	return (
		<>
			<h2>Audit logs</h2>
			<div className="ch-card ch-lightgrey-background flex flex-col p-4 mb-2">
				<div className="flex gap-6 mb-4 ">
					<div style={{ width: 300 }}>
						<SelectControl
							id="clinicId"
							label="Centre"
							value={filters.clinicId || ""}
							options={(clinics ?? []).map((clinic) => ({
								value: clinic.id,
								label: clinic.name
							}))}
							includeNone={true}
							handleChange={(value: string) =>
								handleSelectChange("clinicId", value)
							}
							handleClear={(name: string) => clearFilter(name)}
						/>
					</div>
					<div style={{ width: 300 }}>
						<SelectControl
							id="action"
							label="Action"
							value={filters.action || ""}
							options={actions.map((option) => ({
								value: option,
								label: option
							}))}
							includeNone={true}
							handleChange={(value: string) =>
								handleSelectChange("action", value)
							}
							handleClear={(name: string) => clearFilter(name)}
						/>
					</div>
					<div style={{ width: 300 }}>
						<SelectControl
							id="applicationScreen"
							label="ApplicationScreen"
							value={filters.applicationScreen || ""}
							options={screens.map((option) => ({
								value: option,
								label: option
							}))}
							includeNone={true}
							handleChange={(value: string) =>
								handleSelectChange("applicationScreen", value)
							}
							handleClear={(name: string) => clearFilter(name, "")}
						/>
					</div>
				</div>
				<div className="flex gap-6">
					<div style={{ width: 300 }}>
						<FaceToFaceDoctorSearch
							label="Doctor"
							value={doctorSearch?.displayName}
							onDoctorSelect={handleDoctorChange}
						/>
					</div>
					<div style={{ width: 300 }}>
						<AuditLogUserSearch
							label="User"
							value={usernameSearch}
							onUserSelect={handleUsernameChange}
						/>
					</div>
					<div style={{ width: 300 }}>
						<AuditLogPatientSearch
							label="Patient"
							value={patientSearch?.patientName}
							onPatientSelect={handlePatientChange}
						/>
					</div>

					<TextField
						type="date"
						id="fromDate"
						name="fromDate"
						label="From"
						variant="standard"
						value={fromDateValue}
						size="small"
						slotProps={{
							inputLabel: {
								shrink: true
							},
							htmlInput: {
								max: today
							}
						}}
						onChange={handleFiltersChange}
					/>
					<TextField
						type="date"
						id="toDate"
						name="toDate"
						label="To"
						variant="standard"
						value={toDateValue}
						size="small"
						error={toDateValidationError !== ""}
						helperText={toDateValidationError}
						slotProps={{
							inputLabel: {
								shrink: true
							},
							htmlInput: {
								max: today
							}
						}}
						onChange={handleFiltersChange}
					/>
					<button
						className="ch-button ch-button-secondary"
						onClick={handleSearchClick}
						disabled={toDateValidationError !== ""}
					>
						Search Logs
					</button>
					<button
						className="ch-button ch-button-primary"
						onClick={handleClearClick}
					>
						Clear
					</button>
				</div>
			</div>
		</>
	);
};
